package com.example.readingnotes.data.repository

import com.example.readingnotes.data.remote.api.ApiClient
import com.example.readingnotes.data.remote.dto.LoginWithCodeRequestDto
import com.example.readingnotes.data.remote.dto.OidcCallbackResponseDto
import com.example.readingnotes.data.remote.dto.OidcLogoutResponseDto
import com.example.readingnotes.data.remote.dto.OidcRefreshResponseDto
import com.example.readingnotes.data.remote.dto.ProfileResponseDto
import com.example.readingnotes.data.remote.dto.RegisterRequestDto
import com.example.readingnotes.data.remote.dto.SendVerificationCodeRequestDto
import com.example.readingnotes.data.remote.dto.TotpVerifyRequestDto
import com.example.readingnotes.data.remote.dto.UpdateProfileRequestDto
import com.example.readingnotes.data.remote.dto.UpdateUsernameRequestDto
import com.example.readingnotes.data.remote.dto.UsernameLoginRequestDto
import com.example.readingnotes.data.remote.dto.VerifyEmailRequestDto
import com.example.readingnotes.data.remote.oidc.AuthorizationUrlResult
import com.example.readingnotes.domain.model.AuthState
import com.example.readingnotes.domain.model.TotpSetupResponse
import com.example.readingnotes.domain.model.User
import com.example.readingnotes.domain.model.UserRole
import com.example.readingnotes.domain.repository.AuthRepository
import com.example.readingnotes.domain.repository.UserProfile
import com.example.readingnotes.util.Result

class AuthRepositoryImpl(
    private val apiClient: ApiClient,
) : AuthRepository {
    override suspend fun login(password: String): Result<AuthState> =
        try {
            val response = apiClient.login(password)
            Result.success(AuthState(isAdmin = response.isAdmin))
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun logout(): Result<Unit> =
        try {
            apiClient.logout()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun getAuthState(): Result<AuthState> =
        try {
            val response = apiClient.getAuthState()
            Result.success(AuthState(isAdmin = response.isAdmin))
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun register(username: String, password: String): Result<AuthState> =
        try {
            val response = apiClient.register(RegisterRequestDto(username, password))
            val user =
                User(
                    id = response.id,
                    username = response.username,
                    role = if (response.role == "admin") UserRole.ADMIN else UserRole.PUBLIC,
                    totpEnabled = response.totpEnabled,
                )
            Result.success(AuthState(isAdmin = response.role == "admin", user = user))
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun loginWithUsername(username: String, password: String): Result<AuthState> =
        try {
            val response = apiClient.loginWithUsername(UsernameLoginRequestDto(username, password))
            val user =
                User(
                    id = response.id,
                    username = response.username,
                    role = if (response.role == "admin") UserRole.ADMIN else UserRole.PUBLIC,
                    totpEnabled = response.totpEnabled,
                )
            Result.success(AuthState(isAdmin = response.isAdmin, user = user))
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun verifyTotp(code: String): Result<Boolean> =
        try {
            val response = apiClient.verifyTotp(TotpVerifyRequestDto(code))
            Result.success(response.verified)
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun setupTotp(): Result<TotpSetupResponse> =
        try {
            val response = apiClient.setupTotp()
            Result.success(TotpSetupResponse(secret = response.secret, qrCodeUrl = response.qrCodeUrl))
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun updateUsername(newUsername: String): Result<User> =
        try {
            val response = apiClient.updateUsername(UpdateUsernameRequestDto(newUsername))
            Result.success(
                User(
                    id = response.id,
                    username = response.username,
                    role = if (response.role == "admin") UserRole.ADMIN else UserRole.PUBLIC,
                    totpEnabled = response.totpEnabled,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun checkUsernameAvailable(username: String): Result<Boolean> =
        try {
            val response = apiClient.checkUsernameAvailable(username)
            Result.success(response.available)
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun sendVerificationCode(email: String): Result<Unit> =
        try {
            apiClient.sendVerificationCode(SendVerificationCodeRequestDto(email))
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun verifyEmail(email: String, code: String): Result<AuthState> =
        try {
            val response = apiClient.verifyEmail(VerifyEmailRequestDto(email, code))
            val user =
                User(
                    id = response.user.id,
                    username = response.user.username ?: email,
                    role = if (response.user.role == "admin") UserRole.ADMIN else UserRole.PUBLIC,
                    totpEnabled = response.user.totpEnabled,
                )
            Result.success(
                AuthState(
                    isAdmin = response.user.role == "admin",
                    user = user,
                    totpVerified = true,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun loginWithEmail(email: String, code: String): Result<AuthState> =
        try {
            val response = apiClient.loginWithCode(LoginWithCodeRequestDto(email, code))
            val user =
                User(
                    id = response.user.id,
                    username = response.user.username ?: email,
                    role = if (response.user.role == "admin") UserRole.ADMIN else UserRole.PUBLIC,
                    totpEnabled = response.user.totpEnabled,
                )
            Result.success(
                AuthState(
                    isAdmin = response.user.role == "admin",
                    user = user,
                    totpVerified = true,
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    // ============================================================================
    // OIDC authentication implementation
    // ============================================================================

    override suspend fun oidcLogin(): Result<AuthorizationUrlResult> =
        try {
            val response = apiClient.oidcLogin()
            Result.success(
                AuthorizationUrlResult(
                    url = response.authorization_url,
                    state = response.state,
                    codeVerifier = "", // Code verifier is managed by the backend
                ),
            )
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun oidcCallback(code: String, state: String): Result<AuthState> =
        try {
            val response: OidcCallbackResponseDto = apiClient.oidcCallback(code, state)
            if (!response.success || response.data == null) {
                Result.failure(Exception("OIDC callback failed"))
            } else {
                val user =
                    User(
                        id = response.data.platform_user_id,
                        username = response.data.name ?: response.data.email ?: "",
                        role = UserRole.PUBLIC, // Role is determined by tenant membership
                        totpEnabled = false, // TOTP is managed by the auth platform
                    )
                Result.success(
                    AuthState(
                        isAdmin = false,
                        user = user,
                        totpVerified = true,
                    ),
                )
            }
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun oidcRefresh(): Result<AuthState> =
        try {
            val response: OidcRefreshResponseDto = apiClient.oidcRefresh()
            if (!response.success || response.data == null) {
                Result.failure(Exception("OIDC refresh failed"))
            } else {
                val user =
                    User(
                        id = response.data.platform_user_id,
                        username = response.data.name ?: response.data.email ?: "",
                        role = UserRole.PUBLIC,
                        totpEnabled = false,
                    )
                Result.success(
                    AuthState(
                        isAdmin = false,
                        user = user,
                        totpVerified = true,
                    ),
                )
            }
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun oidcLogout(): Result<Unit> =
        try {
            val response: OidcLogoutResponseDto = apiClient.oidcLogout()
            if (response.success) {
                Result.success(Unit)
            } else {
                Result.failure(Exception(response.message))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }

    // ============================================================================
    // Profile management
    // ============================================================================

    override suspend fun getProfile(): Result<UserProfile> =
        try {
            val response: ProfileResponseDto = apiClient.getProfile()
            if (response.success && response.data != null) {
                Result.success(
                    UserProfile(
                        id = response.data.id,
                        email = response.data.email,
                        username = response.data.username,
                        displayName = response.data.displayName,
                        bio = response.data.bio,
                        profileComplete = response.data.profileComplete,
                        onboardingCompleted = response.data.onboardingCompleted,
                        onboardingStep = response.data.onboardingStep,
                    ),
                )
            } else {
                Result.failure(Exception("Failed to get profile"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }

    override suspend fun updateProfile(
        username: String?,
        displayName: String?,
    ): Result<UserProfile> =
        try {
            val response: ProfileResponseDto =
                apiClient.updateProfile(UpdateProfileRequestDto(username, displayName))
            if (response.success && response.data != null) {
                Result.success(
                    UserProfile(
                        id = response.data.id,
                        email = response.data.email,
                        username = response.data.username,
                        displayName = response.data.displayName,
                        bio = response.data.bio,
                        profileComplete = response.data.profileComplete,
                        onboardingCompleted = response.data.onboardingCompleted,
                        onboardingStep = response.data.onboardingStep,
                    ),
                )
            } else {
                Result.failure(Exception("Failed to update profile"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
}
